/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { ANALYTICS_PERMISSIONS } from "components/strategyDashboard/permissions";
import React, { ReactNode, useState } from "react";
import { useIdToken } from "react-firebase-hooks/auth";
import { Navigate, useLocation, useParams } from "react-router";
import { Alert } from "@mui/material";
import { useTranslation } from "react-i18next";
import { NotFound } from "../components/notFound";
import { authUpdate, TenantPermissions } from "../store/slices/settings";
import { getAuth, ParsedToken } from "firebase/auth";
import {
  useHasOverviewAccessOnAnyTenant,
  useHasTenant,
  getIsMeticulous,
  usePermission,
  useCraneDriverViewPermissions,
  useHasNavigationAccess,
  useHeatOverridesEnabled,
  useCranePagesEnabled,
} from "hooks/permissions";
import { ProvideTenantData, useTenantData } from "contexts/tenant";
import {
  FeatureFlag,
  Permission,
  Tenant,
  useGetMeQuery,
} from "store/api/generatedApi.tsx";
import { skipToken } from "@reduxjs/toolkit/query/react";
import { useIsAdmin, useUserUid } from "hooks/settings.tsx";
import { useAppDispatch } from "store/store.tsx";
import { GlobalLoadingScreen } from "contexts/globalLoadingScreen";
import { isEmailLinkSignin } from "src/utils/isEmailLinkSignin";

interface ParsedClaims extends ParsedToken {
  permissions?: TenantPermissions;
  admin?: string;
  admin_paused?: string;
}

/** Requires an authenticated user, redirecting unauthenticated users to sign-in. */
export const RequireUser = ({ children }: { children: ReactNode }) => {
  const auth = getAuth();
  const location = useLocation();
  const dispatch = useAppDispatch();

  // Meticulous runs with mocked backend requests and no Firebase identity token.
  const isTesting = getIsMeticulous();

  const [claimsLoadedForUid, setClaimsLoadedForUid] = useState<string | null>(
    null
  );

  const [user, loading] = useIdToken(auth, {
    onUserChanged: async (changedUser) => {
      if (!changedUser) {
        return;
      }
      try {
        const claims: ParsedClaims | undefined = (
          await changedUser.getIdTokenResult()
        ).claims;
        if (claims) {
          dispatch(
            authUpdate({
              userUid: changedUser.uid,
              isAdmin: claims.admin === "admin",
              isAdminPaused: claims.admin_paused === "admin",
              tenantPermissions: claims.permissions ?? {},
              displayName: changedUser.displayName ?? "",
              email: changedUser.email ?? "",
            })
          );
        }
      } catch (error) {
        console.error(error);
      } finally {
        setClaimsLoadedForUid(changedUser.uid);
      }
    },
  });

  if (!user && !loading && !isTesting) {
    return <Navigate to="/sign-in" replace state={{ from: location }} />;
  } else if (!user && loading) {
    return <GlobalLoadingScreen />;
  }

  // Email-link users must complete account linking before accessing the app
  if (user && isEmailLinkSignin(user)) {
    return <Navigate to={`/link-account`} replace />;
  }

  // Wait for this user's Redux claims before rendering permission guards.
  if (user && !isTesting && claimsLoadedForUid !== user.uid) {
    return <GlobalLoadingScreen />;
  }

  return children;
};

/** Requires access to the tenant in the URL. */
export const ValidateUser = ({ children }: { children: ReactNode }) => {
  const tenant = useParams().tenant;
  const userUid = useUserUid();
  const hasAccess = useHasTenant(tenant ?? "");

  // A successful query accepts a pending invitation for this tenant.
  const queryResult = useGetMeQuery(
    hasAccess || !userUid || !tenant ? skipToken : { tenantName: tenant }
  );

  if (hasAccess) {
    return children;
  }

  if (queryResult.isError || !hasAccess) {
    return <NotFound />;
  }

  if (queryResult.isLoading) {
    return <GlobalLoadingScreen />;
  }

  return null;
};

/** Requires an accessible tenant URL parameter and provides its tenant context. */
export const RequireTenant = ({ children }: { children: ReactNode }) => {
  const tenant = useParams().tenant;
  const hasAccess = useHasTenant(tenant ?? "");

  // Tenant translations are unavailable until access is verified.
  const { t } = useTranslation();

  if (typeof tenant !== "string") {
    return <Alert severity="error">{t("errorLoadingTenantParams")}</Alert>;
  } else if (hasAccess) {
    return <ProvideTenantData>{children}</ProvideTenantData>;
  } else {
    return <NotFound />;
  }
};

export const RequireCraneDriverAccess = ({
  children,
}: React.PropsWithChildren) => {
  if (usePermission(useCraneDriverViewPermissions())) {
    return children;
  }
  return <NotFound />;
};

export const RequireIsVideoLabellingUser = ({
  children,
}: React.PropsWithChildren) => {
  const isVideoLabellingUser = usePermission(["video_labelling"]);
  if (isVideoLabellingUser) {
    return children;
  } else {
    return <NotFound />;
  }
};
export const RequireIsVideoLabelReviewer = ({
  children,
}: React.PropsWithChildren) => {
  const isVideoLabelReviewer = usePermission(["video_label_reviewing"]);
  if (isVideoLabelReviewer) {
    return children;
  } else {
    return <NotFound />;
  }
};

export const RequireIsScrapTrackingDashboardViewer = ({
  children,
}: React.PropsWithChildren) => {
  const isScrapTrackingDashboardViewer = usePermission([
    "scrap_tracking_dashboard_viewer",
  ]);
  if (isScrapTrackingDashboardViewer) {
    return children;
  } else {
    return <NotFound />;
  }
};

export const RequireIsCraneSupervisor = ({
  children,
}: React.PropsWithChildren) => {
  const isCraneSupervisor = usePermission([
    "crane_supervisor",
    "crane_supervisor_viewer",
  ]);
  if (isCraneSupervisor) {
    return children;
  } else {
    return <NotFound />;
  }
};

export const RequireIsStrategyDashboardAdmin = ({
  children,
}: React.PropsWithChildren) => {
  const isStrategyDashboardAdmin = usePermission(["strategy_dashboard_admin"]);
  if (isStrategyDashboardAdmin) {
    return children;
  } else {
    return <NotFound />;
  }
};

export const RequireAnalyticsAccess = ({
  children,
}: React.PropsWithChildren) => {
  const hasAccess = usePermission(ANALYTICS_PERMISSIONS);
  if (hasAccess) {
    return children;
  } else {
    return <NotFound />;
  }
};

export const RequireOverviewAnyTenantPage = ({
  children,
}: React.PropsWithChildren) => {
  const hasOverviewAccess = useHasOverviewAccessOnAnyTenant();
  return hasOverviewAccess ? children : <NotFound />;
};

export const RequireManager = ({ children }: React.PropsWithChildren) => {
  const isManager = usePermission(["manage_target_baskets"]);
  if (isManager) {
    return children;
  } else {
    return <NotFound />;
  }
};

export const RequireAdmin = ({ children }: React.PropsWithChildren) => {
  const isAdmin = useIsAdmin();
  if (isAdmin) {
    return children;
  } else {
    return null;
  }
};

export const RequireAdminPage = ({ children }: React.PropsWithChildren) => {
  const isAdmin = useIsAdmin();
  if (isAdmin) {
    return children;
  } else {
    return <NotFound />;
  }
};

export const RequireFeatureFlagsPage = ({
  children,
  featureFlags,
}: React.PropsWithChildren<{
  featureFlags: FeatureFlag[];
}>) => {
  const tenantData = useTenantData();

  if (
    featureFlags.every((featureFlag) =>
      tenantData.feature_flags.includes(featureFlag)
    )
  ) {
    return children;
  }
  return <NotFound />;
};

export const RequireHeatOverridesEnabledPage = ({
  children,
}: React.PropsWithChildren) =>
  useHeatOverridesEnabled() ? children : <NotFound />;

export const RequireCranePagesEnabledPage = ({
  children,
}: React.PropsWithChildren) =>
  useCranePagesEnabled() ? children : <NotFound />;

export const RequireFeatureFlags = ({
  children,
  featureFlags,
}: React.PropsWithChildren<{
  featureFlags: FeatureFlag[];
}>) => {
  const tenantData = useTenantData();

  if (
    featureFlags.every((featureFlag) =>
      tenantData.feature_flags.includes(featureFlag)
    )
  ) {
    return children;
  }
  return null;
};

export const RequirePermissions = ({
  children,
  permissions,
  fallback = null,
}: React.PropsWithChildren<{
  permissions: Permission[];
  fallback?: React.ReactNode;
}>) => {
  const hasPermissions = usePermission(permissions);
  if (hasPermissions) {
    return children;
  } else {
    return fallback;
  }
};

export const RequireTenantSettings = ({
  children,
  settings,
}: React.PropsWithChildren<{
  settings: Partial<Tenant>;
}>) => {
  const tenantData = useTenantData();
  const allMatch = (Object.keys(settings) as readonly (keyof Tenant)[]).every(
    (key) => tenantData[key] === settings[key]
  );
  if (allMatch) {
    return children;
  }
  return null;
};

export const RequirePermissionsPage = ({
  children,
  permissions,
}: React.PropsWithChildren<{ permissions: Permission[] }>) => (
  <RequirePermissions permissions={permissions} fallback={<NotFound />}>
    {children}
  </RequirePermissions>
);

export const RequireNavigation = ({ children }: React.PropsWithChildren) =>
  useHasNavigationAccess() ? children : null;
